import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Alert,
  Snackbar,
  Link,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { getCustomers, deleteCustomer } from '../../../app/api';
import { useTranslation } from '../../../app/LanguageContext';
import ConfirmDialog from '../../core/public/ConfirmDialog';

const customerNameLinkSx = {
  color: 'inherit',
  '&:hover': {
    color: 'primary.main',
  },
  '&:focus-visible': {
    borderRadius: 1,
    outline: '2px solid',
    outlineColor: 'primary.main',
    outlineOffset: 2,
  },
};

const CustomersPage = () => {
  const { translate } = useTranslation();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, customerId: null, customerName: '' });
  const navigate = useNavigate();

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const response = await getCustomers();
      setCustomers(response.data?.customers || []);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCustomers();
  }, []);

  const handleDeleteClick = (customerId, customerName) => {
    setConfirmDialog({
      open: true,
      customerId,
      customerName,
    });
  };

  const handleDelete = async () => {
    const { customerId } = confirmDialog;
    setConfirmDialog({ open: false, customerId: null, customerName: '' });

    try {
      await deleteCustomer(customerId);
      setSnackbar({
        open: true,
        message: translate('customers.deleted'),
        severity: 'success',
      });
      loadCustomers();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    }
  };

  const handleDeleteCancel = () => {
    setConfirmDialog({ open: false, customerId: null, customerName: '' });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/customers/new')}
        >
          {translate('common.new')} {translate('customers.title')}
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{translate('customers.name')}</TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{translate('customers.email')}</TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{translate('customers.phone')}</TableCell>
              <TableCell align="right">{translate('customers.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  {translate('common.loading')}
                </TableCell>
              </TableRow>
            ) : customers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  {translate('customers.no_customers')}
                </TableCell>
              </TableRow>
            ) : (
              customers.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>
                    <Link
                      component={RouterLink}
                      to={`/customers/${c.id}/edit`}
                      underline="hover"
                      color="inherit"
                      title={translate('common.edit')}
                      aria-label={`${translate('common.edit')}, ${translate('customers.name')}: ${c.name || '—'}`}
                      sx={customerNameLinkSx}
                    >
                      {c.name}
                    </Link>
                    {c.email && (
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ display: { xs: 'block', sm: 'none' } }}>
                        {c.email}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    {c.email ? (
                      <Tooltip title={c.email}>
                        <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {c.email}
                        </Typography>
                      </Tooltip>
                    ) : '-'}
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{c.phone || '-'}</TableCell>
                  <TableCell align="right">
                    <Tooltip title={translate('common.edit')}>
                      <IconButton sx={{ p: '10px' }} onClick={() => navigate(`/customers/${c.id}/edit`)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={translate('common.delete')}>
                      <IconButton sx={{ p: '10px' }} onClick={() => handleDeleteClick(c.id, c.name)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ConfirmDialog
        open={confirmDialog.open}
        title={translate('customers.confirm_delete_title')}
        message={`${translate('customers.confirm_delete_message')} "${confirmDialog.customerName}"?`}
        onConfirm={handleDelete}
        onCancel={handleDeleteCancel}
        confirmText={translate('common.delete')}
        cancelText={translate('common.cancel')}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomersPage;
